import React, { useEffect, useState } from "react";
import { Modal, ModalDialog, Button, Checkbox, Chip } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postFetch } from "../../../../../../fetch/fetch";
import styles from "./staffmanage.module.css";
import { API, toastOptions, errorText, Position, mmdd, periodText } from "./rosterutils";

//직원명부 "체크한 직원 재알림 보내기" — 체크한 줄 중 미서명 근로계약서(contractState=pending)만 대상. 영상동의서는 여기서 안 보냄 (사용자 결정)
//미리보기(대상 목록) → 순차 재알림(POST /documents/remind/bulk, 만료된 건은 서버가 7일 연장 후 발송) → 건별 결과
interface RemindRow {
    docRowId: number;
    positionId: number;
    name: string;
    branchLabel: string;
    partName: string;
    roundNo: number;
    period: string;
    sentByName: string;
    sentAt: string | null;
    expiresAt: string | null;
    expired: boolean;
    remindCount: number;
    send: boolean;
}

interface RemindResult {
    id: number;
    ok: boolean;
    extended?: boolean;
    remindCount?: number;
    error?: string;
}

//명부 줄 → 재알림 대상 (미서명 근로계약서가 있는 줄만)
export const remindTargetsOf = (rows: Position[]): RemindRow[] =>
    rows.filter((p) => p.contractState === "pending" && p.latestDoc).map((p) => ({
        docRowId: p.latestDoc!.id,
        positionId: p.id,
        name: p.name,
        branchLabel: p.branchLabel,
        partName: p.partName || "",
        roundNo: p.latestDoc!.roundNo,
        period: periodText(p.latestDoc!.periodStart, p.latestDoc!.periodEnd),
        sentByName: p.latestDoc!.sentByName,
        sentAt: p.latestDoc!.sentAt,
        expiresAt: p.latestDoc!.expiresAt,
        expired: !!p.expired,
        remindCount: p.latestDoc!.remindCount || 0,
        send: true
    }));

const RemindModal: React.FC<any> = (props) => {

    const [rows, setRows] = useState<RemindRow[]>([]);
    const [sending, setSending] = useState(false);
    const [results, setResults] = useState<RemindResult[] | null>(null);

    useEffect(() => {
        if (!props.open) return;
        setRows(remindTargetsOf(props.rows || []));
        setResults(null);
        setSending(false);
    }, [props.open]);

    const targets = rows.filter((r) => r.send);

    const send = async () => {
        if (targets.length === 0) return;
        const extendCount = targets.filter((r) => r.expired).length;
        if (!window.confirm(`${targets.length}명에게 서명 재알림(카톡)을 보낼까요?${extendCount ? `\n\n유효기간이 지난 ${extendCount}건은 7일 연장 후 보냅니다.` : ""}`)) return;
        try {
            setSending(true);
            const result: any = await postFetch(`${API}/documents/remind/bulk`, { ids: targets.map((r) => r.docRowId) });
            if (result.message === "success") {
                setResults(result.results || []);
            } else {
                toast.error(errorText(result, "재알림에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setSending(false);
        }
    };

    const cell = (w: number | string, children: any, extra: any = {}) => (
        <div style={{ width: typeof w === "number" ? `${w}px` : w, flexShrink: 0, ...extra }}>{children}</div>
    );

    return (
        <Modal open={props.open} onClose={() => props.onClose(false)}>
            <ModalDialog sx={{ width: "1100px", maxWidth: "96vw", maxHeight: "90vh", overflow: "auto" }}>

                <div className={styles.modalTitle}>
                    체크한 직원 재알림 보내기
                    <span className={styles.subText} style={{ marginLeft: "10px", fontFamily: "Apple_R" }}>
                        체크 {(props.rows || []).length}줄 중 미서명 근로계약서 {rows.length}건
                    </span>
                </div>

                {results === null &&
                    <>
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "auto", maxHeight: "60vh" }}>
                            <div style={{ display: "flex", gap: "10px", padding: "8px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", color: "#888", position: "sticky", top: 0, zIndex: 1, minWidth: "1000px" }}>
                                {cell(40, "보냄")}{cell(90, "이름")}{cell(80, "지점")}{cell(170, "파트")}{cell(200, "문서")}{cell(130, "발송")}{cell(170, "서명 유효기간")}{cell(70, "재알림")}
                            </div>
                            {rows.map((r, idx) => (
                                <div key={r.docRowId} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", borderBottom: "1px solid #f0f0f0", fontSize: "13px", fontFamily: "Apple_R", minWidth: "1000px", background: r.send ? "white" : "#fafafa" }}>
                                    {cell(40, <Checkbox size="sm" checked={r.send} onChange={(e) => setRows((prev) => prev.map((x, i) => (i === idx ? { ...x, send: e.target.checked } : x)))} />)}
                                    {cell(90, <span style={{ fontFamily: "Apple_SB" }}>{r.name}</span>)}
                                    {cell(80, r.branchLabel)}
                                    {cell(170, <span title={r.partName} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.partName || "-"}</span>)}
                                    {cell(200, <span>근로계약서 {r.roundNo}회차<span className={styles.subText} style={{ marginLeft: "6px" }}>{r.period}</span></span>)}
                                    {cell(130, <span className={styles.subText} style={{ fontSize: "12px" }}>{r.sentByName} · {mmdd(r.sentAt)}</span>)}
                                    {cell(170, r.expired
                                        ? <span style={{ color: "#d32f2f", fontFamily: "Apple_SB" }}>{mmdd(r.expiresAt)} 만료됨 → +7일 연장</span>
                                        : <span>{mmdd(r.expiresAt)}까지</span>)}
                                    {cell(70, `${r.remindCount}회`)}
                                </div>
                            ))}
                            {rows.length === 0 && <div style={{ padding: "20px", color: "#9ca3af", fontFamily: "Apple_R" }}>체크한 직원 중 미서명 근로계약서가 없습니다</div>}
                        </div>

                        <div className={styles.modalButtons}>
                            <div className={styles.hint}>재알림 = 유캔싸인이 직원에게 서명 요청 카톡을 다시 보냄 · 유효기간이 지난 건은 7일 연장하고 보냄 · 영상동의서 재알림은 신규직원 TODO에서</div>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <Button variant="plain" color="neutral" onClick={() => props.onClose(false)} disabled={sending}>취소</Button>
                                <Button variant="solid" onClick={send} loading={sending} disabled={targets.length === 0}>{targets.length}건 재알림 보내기</Button>
                            </div>
                        </div>
                    </>
                }

                {results !== null &&
                    <>
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                            <div style={{ display: "flex", gap: "10px", padding: "8px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", color: "#888" }}>
                                {cell(100, "이름")}{cell(170, "파트")}{cell(120, "결과")}{cell("auto", "내용", { flex: 1 })}
                            </div>
                            {results.map((res) => {
                                const r = rows.find((x) => x.docRowId === res.id);
                                return (
                                    <div key={res.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", borderBottom: "1px solid #f0f0f0", fontSize: "13px", fontFamily: "Apple_R" }}>
                                        {cell(100, <span style={{ fontFamily: "Apple_SB" }}>{r ? r.name : res.id}</span>)}
                                        {cell(170, r ? r.partName : "")}
                                        {cell(120, res.ok ? <Chip size="sm" variant="soft" color="success">보냄</Chip> : <Chip size="sm" variant="soft" color="danger">실패</Chip>)}
                                        {cell("auto", <span className={styles.subText} style={{ fontSize: "12px" }}>{res.ok ? `재알림 ${res.remindCount}회${res.extended ? " · 유효기간 +7일" : ""}` : res.error}</span>, { flex: 1 })}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={styles.modalButtons}>
                            <div className={styles.hint}>성공 {results.filter((r) => r.ok).length}건 · 실패 {results.filter((r) => !r.ok).length}건</div>
                            <Button variant="solid" onClick={() => props.onClose(true)}>닫기</Button>
                        </div>
                    </>
                }

            </ModalDialog>
        </Modal>
    );
};

export default RemindModal;
